/**
 * @file index.tsx
 *
 * @fileoverview Displays a fulltext search with an autocomplete.
 */

import React, { useState, useEffect } from 'react';
import { useKeywordSearch } from '~/jobiqo/components/Search/hooks/useKeywordSearch';
import { FormattedMessage, useIntl } from 'react-intl';
import { Autocomplete } from '~/vcl/atoms/01-forms/12-autocomplete';
import { SearchIcon } from '~/vcl/icons/Search';
import HiddenVisually from '~/vcl/atoms/14-w-a11y/02-visually-hidden';

export interface FulltextSearchProps {
  /**
   * Handle submit event for the fulltext search.
   */
  onSubmit: (keyword: string) => void;
  /**
   * Handle submit event for the fulltext search.
   */
  onChange: (keyword: string) => void;
  /**
   * Fulltext
   */
  fulltext?: string;
  /**
   * SolrSearch endpoint.
   */
  endpoint: string;
}

export const FulltextSearch = ({
  onSubmit,
  onChange,
  fulltext = '',
  endpoint,
}: FulltextSearchProps) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState(fulltext);

  // If fulltext updates to empty we need to set it.
  useEffect(() => {
    if (!fulltext) {
      setSearchTerm('');
    }
  }, [fulltext]);

  const keywords = useKeywordSearch(searchTerm ? searchTerm : '', endpoint);
  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
    onChange(event.target.value);
  };

  const handleSelectItem = (value) => {
    setSearchTerm(value);
    onSubmit(value);
  };

  return (
    <Autocomplete
      id="keywords"
      label={
        <HiddenVisually>
          <FormattedMessage
            id="fulltext_autocomplete_label"
            defaultMessage={`Search for a keyword`}
          />
        </HiddenVisually>
      }
      placeholder={intl.formatMessage({
        id: 'fulltext_autocomplete_placeholder',
        defaultMessage: `Search for a keyword`,
      })}
      Icon={
        <SearchIcon
          title={intl.formatMessage({
            id: 'fulltext_autocomplete_placeholder',
            defaultMessage: `Search for a keyword`,
          })}
          color="gray3"
          height="20"
          width="20"
        />
      }
      inputSize={60}
      value={searchTerm}
      onChange={handleSearchTermChange}
      onSelect={handleSelectItem}
      results={keywords.map((keyword: any) => keyword.value)}
    />
  );
};
export default FulltextSearch;
